import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "./SEO";
import { SERVICE_PAGES, servicePath } from "./servicePages";
import { CITY_DATA, cityUrl } from "./cityData";
import "./styles/ServicePage.css";

const SITE = "https://atappliancemaster.com";
const PHONE = "(331) 600-4935";
const TEL = "+13316004935";
const EMAIL = "altantransit@gmail.com";
const HOURS = "Monday - Sunday, 8:00am - 9:00pm"; // confirm this matches your Google Business Profile

export default function CityPage({ slug }) {
  const city = CITY_DATA[slug];
  if (!city) return null;

  const path = cityUrl(slug);
  const nearby = city.near.filter((s) => CITY_DATA[s]);
  const zipText = city.zips.join(", ");

  const faqs = [
    {
      q: `Do you offer same-day appliance repair in ${city.name}, IL?`,
      a: `We offer same-day service in most cases in ${city.name}. Call ${PHONE} to check availability for today.`,
    },
    {
      q: `Which appliances do you repair in ${city.name}?`,
      a: `We repair refrigerators, washers, dryers, dishwashers, ovens, stoves, ranges and wine coolers in ${city.name}, and we also provide commercial appliance repair.`,
    },
    {
      q: `What ZIP codes do you serve in ${city.name}?`,
      a: `In ${city.name} we serve ${zipText} and nearby areas of ${city.county} County.`,
    },
    {
      q: `How much does a service call cost in ${city.name}?`,
      a: `The diagnostic and estimate cost $79. If you go ahead with the repair, the service fee is waived, so you only pay for the repair. Repairs include a 30-day parts and labor warranty.`,
    },
  ];

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "HomeAndConstructionBusiness",
        name: "AT Appliance Repair and Maintenance LLC",
        url: SITE,
        telephone: TEL,
        areaServed: { "@type": "City", name: `${city.name}, IL` },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Service Areas", item: `${SITE}/serviceareas` },
          { "@type": "ListItem", position: 3, name: city.name, item: `${SITE}${path}` },
        ],
      },
    ],
  };

  return (
    <main className="svc-page">
      <SEO
        path={path}
        title={`Appliance Repair in ${city.name}, IL | Same-Day Service | AT Appliance Repair`}
        description={`Appliance repair in ${city.name}, IL for refrigerators, washers, dryers, dishwashers and ovens. Same-day service in most cases. Call ${PHONE}.`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      </Helmet>

      <div className="svc-crumbs">
        <Link to="/">Home</Link> / <Link to="/serviceareas">Service Areas</Link> / {city.name}
      </div>

      <h1>Appliance Repair in {city.name}, IL</h1>
      <p>
        <strong>AT Appliance Repair and Maintenance</strong> repairs refrigerators, washers, dryers,
        dishwashers, ovens and stoves in {city.name}, IL, with same-day service in most cases.
        Call <a href={`tel:${TEL}`}>{PHONE}</a> to schedule.
      </p>
      <p>{city.intro}</p>

      <div className="svc-cta">
        <a className="svc-btn" href={`tel:${TEL}`}>Call {PHONE}</a>
        <Link className="svc-btn svc-btn-alt" to="/contact">Schedule Service</Link>
      </div>

      <h2>Appliance repair services in {city.name}</h2>
      <div className="svc-grid">
        {SERVICE_PAGES.map((s) => (
          <div className="svc-card" key={s.slug}>
            <h3><Link to={servicePath(s.slug)}>{s.name} in {city.name}</Link></h3>
            <p>{s.summary}</p>
          </div>
        ))}
      </div>

      <h2>Serving {city.name} and {city.county} County</h2>
      <p>{city.local}</p>
      <p>ZIP codes we serve in {city.name}: {zipText}.</p>

      <h2>What to expect</h2>
      <ul className="svc-list">
        <li>A qualified technician inspects and diagnoses the problem. The service fee is $79.</li>
        <li>You get a quote for parts and labor before any repair starts.</li>
        <li>If you go ahead with the repair, the service fee is waived.</li>
        <li>Repairs come with a 30-day parts and labor warranty.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      <div className="svc-faq">
        {faqs.map((f) => (
          <details key={f.q}>
            <summary>{f.q}</summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>

      {nearby.length > 0 && (
        <>
          <h2>Also serving nearby cities</h2>
          <ul className="svc-list">
            {nearby.map((s) => (
              <li key={s}>
                <Link to={cityUrl(s)}>Appliance repair in {CITY_DATA[s].name}, IL</Link>
              </li>
            ))}
            <li><Link to="/serviceareas">See all areas we serve</Link></li>
          </ul>
        </>
      )}

      <h2>Contact us</h2>
      <p>
        Headquarters: Plainfield, IL 60586<br />
        Phone: <a href={`tel:${TEL}`}>{PHONE}</a><br />
        Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a><br />
        Hours: {HOURS}
      </p>
    </main>
  );
}